import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { OtpInput } from '../../components/ui/OtpInput';
import { buildScheme } from '../DeepLinkUtils';
import { supabase } from '../../lib/supabase';

export default function EnterInviteCode() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // CRITICAL: Check feature toggle before redirecting to mobile
  React.useEffect(() => {
    const checkFeatureToggle = async () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile && email) {
        // Check feature toggle - only redirect if enabled
        const { shouldRedirectToMobile } = await import('../../lib/featureFlags');
        const shouldRedirect = await shouldRedirectToMobile();
        
        if (shouldRedirect) {
          const mobileDeepLink = buildScheme(`/enter-invite-code?email=${encodeURIComponent(email)}`);
          window.location.replace(mobileDeepLink);
        }
        // If feature toggle disabled, continue with web flow
      }
    };
    
    if (email) {
      checkFeatureToggle();
    }
  }, [email]);

  // Resend cooldown timer
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = async (otpCode: string) => {
    if (!email) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'invite',
      });

      if (verifyError) {
        setError(verifyError.message);
        return;
      }

      // Success - verifyOtp sets the session automatically
      // Redirect to draft-invite-auth - it will use the session
      // Extract tokens from session to pass in URL for compatibility
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const params = new URLSearchParams();
        params.set('access_token', session.access_token);
        params.set('refresh_token', session.refresh_token);
        params.set('type', 'invite');
        navigate(`/draft-invite-auth#${params.toString()}`);
      } else {
        navigate('/draft-invite-auth');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;

    setResendLoading(true);
    setError(null);

    try {
      const { error: resendError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false, // User should already exist from invite
          data: {
            signup_intent: 'draft_student_invite',
          },
        },
      });

      if (resendError) {
        setError(resendError.message);
      } else {
        setResendCooldown(60);
        setError(null);
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <h2 className="text-xl font-semibold text-white mb-2">Invalid Invitation</h2>
              <p className="text-gray-400 text-sm text-center mb-4">
                No email address found. Please use the invitation link from your email.
              </p>
              <Button onClick={() => navigate('/signin')} variant="outline">
                Go to Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111010] flex items-center justify-center p-4">
      <Card className="w-full max-w-sm sm:max-w-md border-[#504A4A] bg-[#221F1F]">
        <CardHeader>
          <CardTitle className="text-white text-center text-lg sm:text-xl">Enter Your Invite Code</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-300 text-center mb-2 text-sm sm:text-base">
            We sent a 6-digit code to:
          </p>
          <p className="text-gray-400 text-center mb-6 sm:mb-8 font-mono text-xs sm:text-sm break-all">
            {email}
          </p>

          <div className="mb-6">
            <OtpInput
              length={6}
              onComplete={handleVerify}
              onChange={setCode}
              error={!!error}
              disabled={loading}
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm text-center mb-4">{error}</p>
          )}

          <Button
            onClick={() => handleVerify(code)}
            disabled={loading || code.length !== 6}
            className="w-full bg-[#e11d48] hover:bg-[#be1e3e] mb-4"
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </Button>

          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={resendLoading || resendCooldown > 0}
              className="text-sm text-[#00D9FF] hover:text-[#00B8D9] disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {resendLoading
                ? 'Sending...'
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend Code'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}








